import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import {
  AdvancedRealTimeChart,
  TickerTape,
  TechnicalAnalysis,
  CryptoCurrencyMarket,
  CryptoCoinsHeatmap,
  StockHeatmap,
  StockMarket,
} from 'react-ts-tradingview-widgets';
import IconicHeader from '../../components/updated/shared/IconicHeader';

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    position: 'relative',
    zIndex: 0,
    overflowY: 'auto',
    outline: 'none',
    overflowX: 'hidden',
    marginTop: '100px',
  },
  tickerContainer: {
    marginTop: 50,
    pointerEvents: 'none',
  },
  widgetContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    marginTop: 20,
    marginLeft: 20,
    marginRight: 20,
  },
  chartSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: 20,
    minHeight: 780,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  chart: {
    flex: 3,
    minHeight: '700px',
  },
  rightSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    justifyContent: 'space-between',
    '&>div': {
      flex: 1,
    },
    [theme.breakpoints.down('md')]: {
      '&>div': {
        height: '500px',
      },
    },
  },
  heatmapSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    marginTop: 20,
    minHeight: '100vh',
  },
}));

// ---- helpers ----

// Common US stock exchange hints (extend as needed)
const STOCK_EXCHANGE_MAP = {
  AAPL: 'NASDAQ',
  AMZN: 'NASDAQ',
  GOOGL: 'NASDAQ',
  META: 'NASDAQ',
  MSFT: 'NASDAQ',
  TSLA: 'NASDAQ',
  NVDA: 'NASDAQ',
  NFLX: 'NASDAQ',
  AMD: 'NASDAQ',
  INTC: 'NASDAQ',
  IBM: 'NYSE',
  ORCL: 'NYSE',
  SHOP: 'NYSE',
  BABA: 'NYSE',
  COIN: 'NASDAQ',
};

// Common crypto tickers — add more if you support more spot pairs
const CRYPTO_SET = new Set([
  'BTC',
  'ETH',
  'USDT',
  'BNB',
  'XRP',
  'ADA',
  'DOGE',
  'SOL',
  'LINK',
  'DOT',
  'MATIC',
  'LTC',
  'BCH',
  'TRX',
  'TON',
  'SHIB',
  'USDC',
]);

function isExplicitTradingViewSymbol(input) {
  return /^[A-Z0-9_]+:[A-Z0-9_\-]+$/i.test((input || '').trim());
}

function isLikelyStock(ticker) {
  const t = (ticker || '').toUpperCase();
  return STOCK_EXCHANGE_MAP[t] != null || !CRYPTO_SET.has(t);
}

function resolveTradingViewSymbol(input) {
  if (!input) return 'BINANCE:BTCUSDT';

  const raw = input.trim().toUpperCase();

  // If user passed "NASDAQ:AAPL" or "BINANCE:BTCUSDT", just use it
  if (isExplicitTradingViewSymbol(raw)) return raw;

  // Crypto like "BTC" or "ETH" → default to Binance USDT pair
  if (!isLikelyStock(raw)) {
    return `BINANCE:${raw}USDT`;
  }

  // Otherwise treat as stock ticker
  const exchange = STOCK_EXCHANGE_MAP[raw] || 'NYSE';
  return `${exchange}:${raw}`;
}

const DEFAULT_TICKERS = [
  { proName: 'BINANCE:BTCUSDT', title: 'Bitcoin' },
  { proName: 'BINANCE:ETHUSDT', title: 'Ethereum' },
  { proName: 'BINANCE:BNBUSDT', title: 'Binance Coin' },
  { proName: 'BINANCE:SOLUSDT', title: 'Solana' },
  { proName: 'BINANCE:LINKUSDT', title: 'Chainlink' },
  { proName: 'BINANCE:XRPUSDT', title: 'XRP' },
  { proName: 'BINANCE:ADAUSDT', title: 'Cardano' },
  { proName: 'BINANCE:DOGEUSDT', title: 'Dogecoin' },
  { proName: 'BINANCE:DOTUSDT', title: 'Polkadot' },
  { proName: 'NASDAQ:AMZN', title: 'Amazon' },
  { proName: 'NASDAQ:AAPL', title: 'Apple' },
  { proName: 'NASDAQ:GOOGL', title: 'Google' },
  { proName: 'NASDAQ:META', title: 'Meta' },
  { proName: 'NASDAQ:TSLA', title: 'Tesla' },
];

const NewAdvancedRealTimeChartComponent = ({ coin }) => {
  const classes = useStyles();
  const [size, setSize] = useState({ width: 1200, height: 800 });
  const [theme, setTheme] = useState(
    localStorage.getItem('selectedTheme') || 'dark'
  );
  const [selectedTab, setSelectedTab] = useState('Markets');

  const handleTabChange = (_event, newValue) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.matchMedia('(max-width: 560px)').matches) {
        setSize({ width: 350, height: 490 });
      } else if (window.matchMedia('(max-width: 990px)').matches) {
        setSize({ width: 450, height: 690 });
      } else {
        setSize({ width: 1200, height: 800 });
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'selectedTheme') {
        setTheme(event.newValue || 'dark');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const tvSymbol = resolveTradingViewSymbol(coin || 'BTC');
  const isStock =
    tvSymbol.startsWith('NASDAQ:') || tvSymbol.startsWith('NYSE:');

  // Dynamic ticker list includes the current selection at the top
  const tickerSymbols = [
    { proName: tvSymbol, title: (coin || 'Asset').toUpperCase() },
    ...DEFAULT_TICKERS.filter((t) => t.proName !== tvSymbol),
  ];

  return (
    <div className={classes.root}>
      <IconicHeader selectedTab={selectedTab} onChange={handleTabChange} />

      <div className={classes.tickerContainer}>
        <TickerTape colorTheme={theme} symbols={tickerSymbols} />
      </div>

      <div className={classes.widgetContainer}>
        {/* Chart Section */}
        <div className={classes.chartSection}>
          <div className={classes.chart}>
            <AdvancedRealTimeChart
              toolbar_bg="#f1f3f6"
              symbol={tvSymbol}
              width="auto"
              height="820"
              theme={theme}
              allow_symbol_change={false}
              settings={true}
            />
          </div>

          {/* Right section */}
          <div className={classes.rightSection}>
            <div>
              <TechnicalAnalysis
                colorTheme={theme}
                symbol={tvSymbol}
                width="auto"
                height="381"
                interval="1D"
                showIntervalTabs={true}
              />
            </div>

            <div>
              {isStock ? (
                <StockMarket colorTheme={theme} width="auto" height="382" />
              ) : (
                <CryptoCurrencyMarket
                  colorTheme={theme}
                  width="auto"
                  height="382"
                />
              )}
            </div>
          </div>
        </div>

        {/* Heatmap Section */}
        <div className={classes.heatmapSection}>
          {isStock ? (
            // Stocks heatmap (TradingView)
            <StockHeatmap colorTheme={theme} height="800" dataSource="S&P 500" />
          ) : (
            // Crypto heatmap
            <CryptoCoinsHeatmap colorTheme={theme} width="auto" height="800" />
          )}
        </div>
      </div>
    </div>
  );
};

export default NewAdvancedRealTimeChartComponent;
